// Browser polyfills
import '@babel/polyfill';
import 'array-flat-polyfill';
import 'whatwg-fetch';

// Libs
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as cloneDeep from 'lodash/cloneDeep';

// Routing
import { Route, routeHistory } from '@nackle/biw-web-router';
import routerMiddleware from './navigation/middleware';
import { routeList, evaluateRouteRedirect, Routes } from './navigation';

// Other
import { Events, EventType } from 'app/events';
import { translationService } from './services'; 
import { auth } from './services/authService/auth';
import { ThemeProvider } from './branding';
import { ACTIVE_THEME_NAME, IS_SEIKO_THEME } from './branding';
import { ONLY_USE_LOCAL_TRANSLATIONS } from './const';
import { Bridge, LocationType } from '@nackle/bridge';

// Components
import { ApolloProvider } from './providers';
import { FeatureFlag, FeatureProvider } from './featureFlags';
import { Layout } from 'antd';
import { AppHeader } from 'app/components/appHeader/AppHeader';
import { AppFooter } from 'app/components/appFooter/AppFooter';
import { getTranslatedString } from './services/translationService/components/Translate';

// Styles
import { Classes } from './styles/classes';
import './styles/app.less';
import classNames from 'classnames';
import { TermsAndConditions } from './components/termsAndConditions';
import { logPageView } from './gql/hooks/mutations/logPageView';
import { BreadcrumbProvider } from './breadcrumbs/BreadcrumbContext';
import { NotificationCarousel } from './notifications/NotificationCarousel';
import { NotificationProvider } from './notifications/NotificationsContext';
import { flags } from './featureFlags/flags';
import { AUTH_ENV, Environment } from './environments';

/************************************
 *  App (root level component)
 ************************************/

const App = () => {
    const [ initializationComplete, setInitializationComplete ] = useState( false );
    const [ brandingClass, setBrandingClass ] = useState( ACTIVE_THEME_NAME );
    const [ initialPopupsShown, setInitialPopupsShown ] = useState( false );

    useEffect( () => {
        window.changeAppBrand = ( brand ) => setBrandingClass( brand );
    }, [] );

    // keep in mind when adding domains that we check substrings, so seikovisionpro needs to list before seikovision for example
    const domainScriptConfig = {
        localhost: '0195f181-de80-7460-bc57-1379867261f8', // change this for whatever testing
        'biwlearning-hoya': '0195f181-de80-7460-bc57-1379867261f8',   
        seikovisionpro: '0194bbea-505a-7fbe-af7d-5b45bad58857', 
        seikovision: '0193d4b4-46fe-7f56-9caa-e2a5689a7982', 
        hoyavisioncarelearningcenter: '01962000-91ea-78ce-86f0-11088bccd693', 
        hoyavisionlearningcenter: '01946f9a-a875-7498-b747-ce98a255ef08', 
        hoyavision: 'f83384b6-73a9-4b31-abd6-181635764427', 
            
    };

    const loadOneTrust = () => {

        let scriptId = Object.entries( domainScriptConfig ).find( ( [ k, v ] ) => window.location.host.includes( k ) )?.[ 1 ];
        if ( !scriptId ) {
            console.warn( 'Could not load onetrust script' );
            return;
        }

        if ( AUTH_ENV != Environment.PROD ) {
            scriptId.concat( '-test' );
        }

        const h = document.getElementsByTagName( 'head' )[ 0 ];
        const first = h.children[ 0 ];

        const onetrustScript = document.createElement( 'script' );
        onetrustScript.type = 'text/javascript';
        onetrustScript.charset = 'UTF-8';
        onetrustScript.setAttribute( 'data-domain-script', scriptId );
        onetrustScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';

        h.insertBefore( onetrustScript, first ); 
    };
    
    /**
     * Attempts to load the site translations for the user's locale found in their
     * auth state. This should automatically update the text on the site if translations
     * are found.
     */
    const loadTranslationsForUser = useCallback( async ( state = undefined ) => {
     
        
        // Now that a user has supposedly signed in and we have their auth state, use their
        // locale to fetch new translations to use on the site.
        const translationsRetrieved = await translationService.fetchTranslations( { attemptExternal: !ONLY_USE_LOCAL_TRANSLATIONS } );
        if ( !translationsRetrieved ) {
            console.warn( `No translations available for language '${ translationService.localeCode }'.` );
        }

        loadOneTrust();
    }, [] );

    useEffect( () => {
        ( async () => {
            // This component is only rendered once when the user visits the page (login
            // or not). Because of this, we can use this callback as a way to begin any
            // necessary app/state initialization. We also know that if this component gets
            // mounted, our services are available and ready for use as needed.

            await Events.invoke( EventType.STARTUP, { debug: false } );
            setInitializationComplete( true );
        } )();
    }, [] );

    useEffect( () => {
        // Attempt to fetch the user's data if they already have an existing session.
        // This may trigger auth events updating our webapp's understanding of the
        // user's session.
        auth.getUser().then( () => {
            routeHistory.refresh();
        } );
    }, [] );

    const notificationsRef = useRef();

    /**
     * A callback used to handle redirects when Bridge receives an authentication update
     * like when the user signs in or out.
     */
    const handleSetLocation = useCallback( ( args ) => {
        switch ( args.type ) {
            case LocationType.SIGN_OUT:
                args.location = Routes.Home.buildPath();
                break;
        }
    }, [] );

    const setDocumentTitle = useCallback( ( translationKey, fallbackTitle ) => {  
        const brandLabel = ( ACTIVE_THEME_NAME === 'seiko' ) ? 'Seiko' : 'Hoya';

        // Attempt to get the translations for the title but use fallback title if not available.
        const pageTitle = ( translationKey )
            ? getTranslatedString( translationKey ) || fallbackTitle
            : fallbackTitle;

        // Update the document title potentially including the active brand.
        document.title = ( pageTitle )
            ? `${ pageTitle } | ${ brandLabel }`
            : brandLabel;
    }, [] );

    // Since SEIKO doesn't have Terms & Conditions, we fire off notifications here
    useEffect( () => {
        if ( IS_SEIKO_THEME ) {
            notificationsRef?.current?.open();
        }
    }, [] );

    function onTermsClose() {
        if ( initialPopupsShown ) {return;}

        setInitialPopupsShown( true );
        notificationsRef?.current?.open();
    }

    // Set the favicon based on the theme
    useEffect( () => {
        if ( IS_SEIKO_THEME ) {
            document.getElementById( 'favicon' ).href = '/favicons/seiko-favicon.png';
        } else {
            document.getElementById( 'favicon' ).href = '/favicons/favicon.ico';
        }
    }, [] );

    return (
        <ApolloProvider>
            { /* Render the page components chosen by the router. */ }
            <Route render={ ( route ) => {
                const routeData = route?.match;

                // Update the window's title using the route names unless we are currently on an auth URL
                // like /oidc_signin. Bridge should take us away from these auth URLs if the user is on one.
                if ( !Bridge.isAuthUrl( auth ) ) {
                    setDocumentTitle( routeData?.translationKey, routeData?.documentTitle );
                }

                // If there is a redirect path found, that suggests the user shouldn't be on this page and
                // shouldn't be able to see the page (used below when considering page render).
                const routeRedirectPath = evaluateRouteRedirect( routeData?.redirect );
                const shouldRedirect = Boolean( routeRedirectPath );

                // Determine if the user needs to be authenticated or is already authenticated.
                const isKnownPage = Boolean( routeData );

                // Decide whether or not to render the page.
                const canRenderRouteComponent = isKnownPage && !shouldRedirect && initializationComplete;

                return (
                    <FeatureProvider>
                        <Bridge
                            auth={ auth }
                            onSetLocation={ handleSetLocation }
                            onAuthStateChange={ loadTranslationsForUser }
                        >
                            <Layout className={ Classes.APP }>
                                <BreadcrumbProvider>
                                    <NotificationProvider>
                                        <ThemeProvider value={ ACTIVE_THEME_NAME }>
                                            <div className={ classNames( 'app-content', brandingClass ) } >
                                                { /* Application Header */ }
                                                <AppHeader />

                                                { /* Render the page content depending on the route. */ }
                                                { ( canRenderRouteComponent )
                                                    ? ( React.cloneElement(
                                                        route.component,
                                                        { className: getRouteClassName( route.pathname, 'view' ) },
                                                    ) )

                                                    // If we ever can't render the route for any reason, render a placeholder component
                                                    // to fill the page.
                                                    : ( <div id="no-page-content" style={ { flexGrow: '1' } } /> )
                                                }

                                                { /* Application Footer */ }
                                                <AppFooter route={ route } />

                                                <TermsAndConditions autoDisplay onClose={ onTermsClose } />

                                                <FeatureFlag name={ flags.NOTIFICATIONS } >
                                                    <NotificationCarousel autoDisplay ref={ notificationsRef } showFeatured /> 
                                                </FeatureFlag>

                                            </div>
                                        </ThemeProvider>   
                                    </NotificationProvider> 
                                </BreadcrumbProvider>
                            </Layout>
                        </Bridge>
                    </FeatureProvider>
                );
            } } />
        </ApolloProvider>
    );
};

const getRouteClassName = ( pathname, prefix ) => {
    const classList = [ prefix ];

    prefix = `${ prefix }-`;
    pathname.substring( 1 ).split( '/' ).forEach( ( part ) => {
        classList.push( `${ prefix }${ part }` );
        prefix = `${ prefix }${ part }-`;
    } );

    return classList.join( ' ' );
};

/*********************************
 *  Connect Routing
 *********************************/

routeHistory.applyRoutes( routeList );
routeHistory.applyMiddleware( routerMiddleware ); 

// const scrollTracker = {};

routeHistory.addEventListener( 'changed', ( _action, newRoute, prevRoute ) => {

    if ( newRoute?.id != prevRoute?.id ) {

        logPageView( { pageUrl: window.location.href, pageType: newRoute.match.key } );

    }
    // Each time the route history changes, store the new state for potential
    // future referencing.
    routeHistory.route = cloneDeep( newRoute );

    // Trigger page view event if the route was recognized.
    const isRecognizedRoute = Boolean( newRoute?.match );
    if ( isRecognizedRoute ) {
        Events.invoke( EventType.PAGE_VIEW, {
            url: newRoute.pathname,
            title: newRoute?.match?.title,
        } );
    }
} );


// Handle initial page scrolling. The `biw-web-router` doesn't do anything
// involving page scrolling, and without this, your scroll position is the
// same when going between pages (for example, if you are at the bottom of
// one page, and navigate to a new one, you will be at the bottom of that
// one too).
routeHistory.addEventListener( 'changing', ( _action, newRoute, prevRoute ) => {
    // When leaving a route, update the scroll tracker object to remember their
    // last scroll location.
    // if ( prevRoute?.id ) {
    //     scrollTracker[ prevRoute.id ] = { x: window.scrollX, y: window.scrollY };
    // }

    // Scroll to the top of the page. Temporarily disable scroll transitions.
    window.document.body.style.setProperty( 'scroll-behavior', 'auto' );
    window.scrollTo( 0, 0 );
    window.document.body.style.removeProperty( 'scroll-behavior' );

    // Check scroll tracker to see if their was a scroll position stored for the
    // new route the user is visiting.
    // if ( newRoute?.id ) {
    //     const lastScrollPos = scrollTracker[ newRoute.id ];
    //     const shouldReturnToLastScrollPos = Boolean( lastScrollPos ) && Boolean( newRoute?.match?.trackScrollPosition );
    //     if ( shouldReturnToLastScrollPos ) {
    //         window.scrollTo( lastScrollPos.x, lastScrollPos.y );
    //         console.log( 'Returning to last scroll position...', lastScrollPos );
    //     }
    // }
} );

export default App;
